<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { useNotifyStore } from '@/stores'
import { uuid } from '@/composables'
import { STATUS_CODES } from '@/constants'
import { formatBytes } from '@/utils'
import { AdminRegistryConfig } from '@/types/admin'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'

defineOptions({ name: 'AdminRegistryHome' })

const router = useRouter()
const { t } = useI18n()
const notifyStore = useNotifyStore()

const loading = ref(true)
const registries = ref<AdminRegistryConfig[]>([])
const searchQuery = ref('')
const actionId = ref<number | null>(null)
const confirmAction = ref<{ id: number; type: 'suspend' | 'unsuspend' } | null>(null)

const formatDate = (dateStr: string): string => {
  return new Date(dateStr).toLocaleDateString()
}

async function fetchRegistries() {
  loading.value = true
  try {
    const res = await useAxios<null, AdminRegistryConfig[]>('GET', '/admin/registry', true)
    if (res && res.status === STATUS_CODES.OK && res.data) {
      const parsed = AdminRegistryConfig.array().safeParse(res.data)
      if (parsed.success) {
        registries.value = parsed.data
      } else {
        console.warn('[AdminRegistryHome] Zod parse error:', parsed.error)
      }
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'AdminRegistryHome' })
  } finally {
    loading.value = false
  }
}

const filteredRegistries = computed(() => {
  if (!searchQuery.value.trim()) return registries.value
  const query = searchQuery.value.toLowerCase()
  return registries.value.filter(r =>
    r.slug.toLowerCase().includes(query) ||
    r.account_email.toLowerCase().includes(query) ||
    String(r.account_id).includes(query)
  )
})

function navigateToDetail(id: number) {
  router.push({ name: 'dashboardAdminRegistryDetail', params: { id } })
}

function promptAction(id: number, type: 'suspend' | 'unsuspend') {
  confirmAction.value = { id, type }
}

function cancelAction() {
  confirmAction.value = null
}

function statusLabel(status: string): string {
  const key = `dashboard.admin.registry.statuses.${status}`
  const label = t(key)
  return label === key ? status : label
}

async function executeAction() {
  if (!confirmAction.value) return
  const { id, type } = confirmAction.value
  const registry = registries.value.find(r => r.id === id)
  if (!registry) return

  confirmAction.value = null
  actionId.value = id

  try {
    const res = await useAxios<null, null>('POST', `/admin/registry/${id}/${type}`, true)
    if (res && res.status === STATUS_CODES.NO_CONTENT) {
      registry.status = type === 'suspend' ? 'suspended' : 'active'
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: t(`dashboard.admin.registry.actions.${type}Success`),
        message: registry.slug
      })
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: `AdminRegistryHome:${type}` })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t(`dashboard.admin.registry.actions.${type}`),
      message: getErrorMessage(err as Error)
    })
  } finally {
    actionId.value = null
  }
}

onMounted(() => {
  fetchRegistries()
})
</script>

<template>
  <BaseDashboardPage
    :title="t('dashboard.admin.registry.title')"
    :subtitle="t('dashboard.admin.registry.subtitle')"
    icon="mdi-docker"
    icon-color="teal"
  >
    <template #actions>
      <v-btn
        icon
        variant="text"
        :loading="loading"
        @click="fetchRegistries"
      >
        <v-icon icon="mdi-refresh" />
      </v-btn>
    </template>

    <!-- Search Bar -->
    <div class="search-bar">
      <v-text-field
        v-model="searchQuery"
        prepend-inner-icon="mdi-magnify"
        :placeholder="t('dashboard.admin.registry.searchPlaceholder')"
        variant="outlined"
        density="compact"
        hide-details
        class="search-input"
      />
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <v-progress-circular indeterminate size="48" color="primary" />
    </div>

    <!-- Empty State -->
    <div v-else-if="filteredRegistries.length === 0" class="empty-state">
      <v-icon icon="mdi-docker" size="64" />
      <h3>{{ t('dashboard.admin.registry.empty') }}</h3>
    </div>

    <!-- Registry Table -->
    <div v-else class="table-container">
      <table class="registry-table">
        <thead>
          <tr>
            <th>{{ t('dashboard.admin.registry.columns.slug') }}</th>
            <th>{{ t('dashboard.admin.registry.columns.account') }}</th>
            <th>{{ t('dashboard.admin.registry.columns.region') }}</th>
            <th>{{ t('dashboard.admin.registry.columns.status') }}</th>
            <th>{{ t('dashboard.admin.registry.columns.storage') }}</th>
            <th>{{ t('dashboard.admin.registry.columns.created') }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="registry in filteredRegistries"
            :key="registry.id"
            class="table-row clickable"
            @click="navigateToDetail(registry.id)"
          >
            <td class="name-cell">
              <span class="registry-slug">{{ registry.slug }}</span>
            </td>
            <td class="account-cell">
              <span class="account-email">{{ registry.account_email }}</span>
              <span class="account-id">#{{ registry.account_id }}</span>
            </td>
            <td class="region-cell">
              <span class="region-chip">{{ registry.region }}</span>
            </td>
            <td class="status-cell">
              <span class="status-badge" :class="registry.status">{{ statusLabel(registry.status) }}</span>
            </td>
            <td class="storage-cell mono">{{ formatBytes(registry.storage_bytes) }}</td>
            <td class="date-cell">{{ formatDate(registry.created_at) }}</td>
            <td class="action-cell">
              <!--
                quota_exceeded is usage-driven and clears itself once the account
                is back under its cap, so it gets no unsuspend button. Suspend
                stays available on it as the operator override for abuse.
              -->
              <button
                v-if="registry.status === 'active' || registry.status === 'quota_exceeded'"
                class="action-btn suspend-btn"
                :disabled="actionId === registry.id"
                @click.stop="promptAction(registry.id, 'suspend')"
              >
                <v-icon icon="mdi-lock" size="16" />
                {{ t('dashboard.admin.registry.actions.suspend') }}
              </button>
              <button
                v-else-if="registry.status === 'suspended'"
                class="action-btn unsuspend-btn"
                :disabled="actionId === registry.id"
                @click.stop="promptAction(registry.id, 'unsuspend')"
              >
                <v-icon icon="mdi-lock-open" size="16" />
                {{ t('dashboard.admin.registry.actions.unsuspend') }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Confirmation Dialog -->
    <div v-if="confirmAction" class="confirm-overlay" @click.self="cancelAction">
      <div class="confirm-dialog">
        <p class="confirm-message">
          {{
            confirmAction.type === 'suspend'
              ? t('dashboard.admin.registry.actions.suspendConfirm')
              : t('dashboard.admin.registry.actions.unsuspendConfirm')
          }}
        </p>
        <div class="confirm-actions">
          <button class="action-btn cancel-btn" @click="cancelAction">
            {{ t('common.cancel') }}
          </button>
          <button
            class="action-btn"
            :class="confirmAction.type === 'suspend' ? 'suspend-btn' : 'unsuspend-btn'"
            @click="executeAction"
          >
            {{ t('common.confirm') }}
          </button>
        </div>
      </div>
    </div>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
.search-bar {
  margin-bottom: 24px;
}

.search-input {
  max-width: 400px;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
}

.empty-state {
  .v-icon {
    color: rgba(120, 163, 255, 0.3);
    margin-bottom: 16px;
  }

  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 8px;
  }
}

.table-container {
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
}

.registry-table {
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 14px 16px;
    text-align: left;
  }

  th {
    font-size: 0.75rem;
    font-weight: 600;
    color: rgba(200, 218, 255, 0.5);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    background: rgba(0, 0, 0, 0.15);
    border-bottom: 1px solid rgba(120, 163, 255, 0.1);
  }
}

.table-row {
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &.clickable {
    cursor: pointer;

    &:hover {
      background: rgba(255, 255, 255, 0.02);
    }
  }
}

.name-cell .registry-slug {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.account-cell {
  .account-email {
    display: block;
    font-size: 0.85rem;
    color: rgba(200, 218, 255, 0.8);
  }

  .account-id {
    display: block;
    font-size: 0.75rem;
    color: rgba(200, 218, 255, 0.4);
  }
}

.region-chip {
  display: inline-flex;
  padding: 3px 10px;
  background: rgba(0, 176, 255, 0.1);
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  color: #64b5f6;
}

.date-cell {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
}

.mono {
  font-family: monospace;
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.7);
}

.status-badge {
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;

  &.active {
    background: rgba(76, 175, 80, 0.15);
    color: #81c784;
  }

  &.suspended {
    background: rgba(255, 152, 0, 0.15);
    color: #ffb74d;
  }

  &.deactivated {
    background: rgba(158, 158, 158, 0.15);
    color: #bdbdbd;
  }

  &.quota_exceeded {
    background: rgba(255, 167, 38, 0.15);
    color: #ffa726;
  }
}

.action-cell {
  text-align: right;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: none;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.suspend-btn {
  background: rgba(255, 152, 0, 0.15);
  color: #ffb74d;

  &:hover:not(:disabled) {
    background: rgba(255, 152, 0, 0.25);
  }
}

.unsuspend-btn {
  background: rgba(76, 175, 80, 0.15);
  color: #81c784;

  &:hover:not(:disabled) {
    background: rgba(76, 175, 80, 0.25);
  }
}

.cancel-btn {
  background: rgba(120, 163, 255, 0.08);
  color: rgba(200, 218, 255, 0.6);

  &:hover {
    background: rgba(120, 163, 255, 0.15);
  }
}

.confirm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.confirm-dialog {
  background: rgb(15, 23, 48);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 12px;
  padding: 24px;
  max-width: 420px;
  width: 90%;
}

.confirm-message {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 20px;
  line-height: 1.5;
}

.confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* Light Theme */
.v-theme--lightTheme {
  .table-container {
    background: rgba(255, 255, 255, 0.8);
    border-color: rgba(28, 110, 227, 0.1);
  }

  .registry-table th {
    background: rgba(0, 0, 0, 0.02);
    color: rgba(15, 23, 42, 0.5);
  }

  .name-cell .registry-slug {
    color: rgba(15, 23, 42, 0.9);
  }

  .account-cell .account-email {
    color: rgba(15, 23, 42, 0.7);
  }

  .account-cell .account-id {
    color: rgba(15, 23, 42, 0.4);
  }

  .date-cell {
    color: rgba(15, 23, 42, 0.6);
  }

  .confirm-dialog {
    background: rgb(255, 255, 255);
    border-color: rgba(28, 110, 227, 0.15);
  }

  .confirm-message {
    color: rgba(15, 23, 42, 0.85);
  }
}

@media (max-width: 768px) {
  .search-input {
    max-width: 100%;
  }

  .table-container {
    overflow-x: auto;
  }

  .registry-table {
    min-width: 800px;
  }
}
</style>
